// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import Layout from 'antd/lib/layout';

import { CombinedState, ObjectType } from 'reducers';
import { ObjectState } from 'cvat-core-wrapper';
import {
    collapseSidebar as collapseSidebarAction,
    lockAllAnnotations,
    switchPlay,
    updateAnnotationsAsync,
    updateJobAttrbutes,
} from 'actions/annotation-actions';
import Collapse from 'antd/lib/collapse';
import getLabelDisplayName from 'utils/label-display';
import { frameToRelativeSeconds, getTimelineFrameRate } from 'utils/media-time';
import { Col, InputNumber, Row, Select, Slider } from 'antd';

import { getFilledFullText_V2, config_V2, toDisplay_V2 } from '../phase2-annotation-conf';
import { getFilledFullText_V1, config_V1, toDisplay_V1 } from '../phase2-annotation-conf_v1';
import { getFilledFullText_phase3, config_phase3, toDisplay_phase3 } from '../phase3-annotation-conf';
import BuildTimeList from './objects-side-bar/build-time-list';
import { CaretLeftFilled } from '@ant-design/icons';
import AudioPlayButton from 'components/annotation-page/person-question-modal/audio-play-button';
import AnnotationVoiceInput from 'components/voice-input/annotation-voice-input';
import AutoPopup from './auto-popup';
interface OwnProps {}

interface StateToProps {
    jobInstance: any;
    allStatesFrameImages: any[];
    allStates: any[];
    jobAttributeSpec: any[];
    jobAttributes: any;
    audioData: ArrayBuffer;
    dataFrameRate: number | null;
}

interface DispatchToProps {
    collapseSidebar(): void;
    lockAllObjects(): void;
    onSwitchPlay(playing: boolean): void;
    onUpdateAnnotations(states: ObjectState[]): void;
    onUpdateJobAttributes(id: number, val: string): void;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: {
                instance: jobInstance,
                attributes: { self: jobAttributeSpec },
            },
            annotations: { allStates, allStatesFrameImages, jobAttributes },
            player: {
                audio: { data: audioData },
            },
        },
    } = state;

    return {
        jobInstance,
        allStatesFrameImages,
        allStates,
        audioData,
        dataFrameRate: jobInstance.dataFrameRate,
        jobAttributeSpec,
        jobAttributes,
    };
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        collapseSidebar(): void {
            dispatch(collapseSidebarAction());
        },
        lockAllObjects(): void {
            dispatch(lockAllAnnotations());
        },
        onSwitchPlay(playing: boolean): void {
            dispatch(switchPlay(playing));
        },
        onUpdateAnnotations(states: ObjectState[]): void {
            dispatch(updateAnnotationsAsync(states));
        },
        onUpdateJobAttributes(id: number, val: string): void {
            dispatch(updateJobAttrbutes(id, val));
        },
    };
}

function QuestionsSideBar(props: StateToProps & DispatchToProps & OwnProps): JSX.Element {
    const {
        allStatesFrameImages,
        allStates,
        audioData,
        dataFrameRate,
        jobAttributeSpec,
        jobAttributes,
        jobInstance,
        onUpdateAnnotations,
        onUpdateJobAttributes,
    } = props;
    const frameRate = getTimelineFrameRate(dataFrameRate);

    // Based on Phase2A Version, we will use different configurations
    const getFilledFullText = jobInstance.phase.includes("phase3") ? getFilledFullText_phase3 : jobInstance.phase2a_version === '2' ? getFilledFullText_V2 : getFilledFullText_V1;
    const phaseConf = jobInstance.phase.includes("phase3") ? config_phase3 : jobInstance.phase2a_version === '2' ? config_V2 : config_V1;
    const toDisplay = jobInstance.phase.includes("phase3") ? toDisplay_phase3 : jobInstance.phase2a_version === '2' ? toDisplay_V2 : toDisplay_V1;



    const NUM_POPUPS=3;

    const audio = useRef<HTMLAudioElement[]>([]);
    const audiolisteners = useRef<((e: any) => void)[]>([]);

    const [audioBlobURL, setAudioBlobURL] = useState<string>('');
    const [audioPlayingIdx, setAudioPlayingIdx] = useState(-1);

    const people = allStates.filter((state) => state.label.name.startsWith('person:'));
    useEffect(() => {
        if (audioData) {
            const blb = new Blob([audioData], { type: 'audio/mp4' });
            setAudioBlobURL(URL.createObjectURL(blb));
        }
    }, [audioData]);

    const playAudio = (idx: number) => {
        audio.current.forEach((rf, i) => {
            if (audioPlayingIdx === -1) {
                if (i === idx) {
                    rf.currentTime = frameToRelativeSeconds(people[idx].frame, jobInstance.startFrame, frameRate);
                    rf.play();
                    setAudioPlayingIdx(i);
                }
            } else {
                rf.pause();
                setAudioPlayingIdx(-1);
            }
        });
    };

    const [croppedImageMap, setCroppedImageMap] = useState<any>(null);
    useEffect(() => {
        // cropped images of the people
        const peopleIndices: number[] = [];
        allStates.forEach((state, idx) => {
            // person tags which are NOT audio selection
            if (state.label.name.startsWith('person:') && state.objectType !== ObjectType.AUDIOSELECTION) {
                peopleIndices.push(idx);
            }
        });
        const images = peopleIndices.map(() => new Image());
        Promise.all(
            images.map(async (image, i) => {
                const cnv = document.createElement('canvas');
                const ctx = cnv.getContext('2d');

                const p1X = Math.min(allStates[peopleIndices[i]].points[0], allStates[peopleIndices[i]].points[2]);
                const p2X = Math.max(allStates[peopleIndices[i]].points[0], allStates[peopleIndices[i]].points[2]);

                const p1Y = Math.min(allStates[peopleIndices[i]].points[1], allStates[peopleIndices[i]].points[3]);
                const p2Y = Math.max(allStates[peopleIndices[i]].points[1], allStates[peopleIndices[i]].points[3]);

                const sX = p1X,
                    sY = p1Y,
                    sW = p2X - p1X,
                    sH = p2Y - p1Y;

                cnv.width = sW;
                cnv.height = sH;
                return await new Promise((resolve) => {
                    image.onload = () => {
                        ctx?.drawImage(image, sX, sY, sW, sH, 0, 0, sW, sH);
                        const cropped = cnv.toDataURL();
                        cnv.remove();
                        resolve(cropped);
                    };
                    image.src = URL.createObjectURL(allStatesFrameImages[peopleIndices[i]]);
                });
            }),
        ).then((imgs) =>
            setCroppedImageMap(
                imgs.reduce((acc: any, cur, idx) => {
                    acc[allStates[peopleIndices[idx]].clientID] = cur;
                    return acc;
                }, {}),
            ),
        );
    }, [allStates, allStatesFrameImages]);

    const checkPhase = ['phase2a', 'phase2b'].includes(jobInstance.phase) ? 'phase2' : 'phase3';

    const doesNotAppearCheck = (states: any) => {
        const arr: number[] = [];
        const anyTrue = states.some((state: any, index: number) => {
            let attributes = state.label.attributes.filter((attr: any) => attr.name.startsWith(checkPhase) && attr.name.endsWith('.doesnotappear'));
            let values = attributes.map((attr: any) => state.attributes[attr.id]);
            let finalStateValue = values.every((val: any) => val === 'true');

            if (finalStateValue) {
                arr.push(index);
            }

            return finalStateValue; // .some will return true if any finalStateValue is true
        });
        return anyTrue;
    };

    const popupPhases = ['phase2a', 'phase3a'];
    const toDisplayQuestions =
        (
            popupPhases.includes(jobInstance.phase)
                ? people.every((state) =>
                    [...Array(NUM_POPUPS).keys()]
                        .map((idx) => {
                            const prefix = `${jobInstance.phase}.popup.${idx}.`;

                            // doesnotappear or doesnotcontribute is true
                            const skipPopup =
                                state.label.attributes.some(
                                    (attr: any) =>
                                        attr.name === prefix + 'doesnotappear' &&
                                        ['true'].includes(state.attributes[attr.id]),
                                ) ||
                                state.label.attributes.some(
                                    (attr: any) =>
                                        attr.name === prefix + 'doesnotcontribute' &&
                                        ['true'].includes(state.attributes[attr.id]),
                                );
                            // all the popups have been filled
                            const allRequiredFilled = state.label.attributes
                                .filter(
                                    (attr: any) =>
                                        attr.name.startsWith(prefix) &&
                                        !attr.nullable &&
                                        toDisplay(
                                            state.label.attributes,
                                            jobAttributeSpec,
                                            state.attributes,
                                            jobAttributes,
                                            attr,
                                            allStates,
                                        ),
                                )
                                .every((attr: any) => state.attributes[attr.id] !== '');
                            return skipPopup || allRequiredFilled;
                        })
                        .every(Boolean),
                )
                : true // phases without popups can always display questions
        ) && !doesNotAppearCheck(people);

    // We define a new state for only the textboxes since if we update the state directly,
    // the component will re-render, this causes the cursor to jump to the end of the input field every time
    const [textboxStates, setTextboxStates] = useState<{ [key: string]: string }>(
        people.reduce((acc, state) => {
            state.label.attributes
                .filter((attr: any) => attr.inputType === 'text')
                .forEach((attr: any) => {
                    acc[attr.id] = state.attributes[attr.id];
                });
            return acc;
        }, {} as { [key: string]: string }),
    );

    const handleTextboxChange = (attrId: string, value: string, stateIndex: number) => {
        // hack to create copy of object
        let oldStates = JSON.parse(JSON.stringify(textboxStates));
        // set new values in the copy and update the state
        oldStates[attrId] = value;
        setTextboxStates(oldStates);

        // update the state in the main state
        people[stateIndex].attributes = {
            ...people[stateIndex].attributes,
            [attrId]: value,
        };

        onUpdateAnnotations([people[stateIndex]]);
    };

    const doesPersonContribute = (person: any) => {
        if(person.label.attributes.filter((attr: any) => attr.name.startsWith(checkPhase) && attr.name.endsWith('.doesnotcontribute')).length === 0) {
            return true;
        }
        // if everything is true, then the person does not contribute
        return !person.label.attributes
            .filter((attr: any) => attr.name.startsWith(checkPhase) && attr.name.endsWith('.doesnotcontribute'))
            .every((attr: any) => person.attributes[attr.id] === 'true');
    };

    const personDoesNotConributeOrAppear = (state: any) => {
        let doesNotContributeValues: any = [];
        let doesNotAppearValues: any = [];
        let doesNotAppearAttributes = state.label.attributes.filter((attr: any) => attr.name.startsWith(checkPhase) && attr.name.endsWith('doesnotappear'));
        doesNotAppearAttributes.forEach((attr: any) => {
            // get popup number
            let popupNumber = parseInt(attr.name.split('.')[2]);
            doesNotAppearValues[popupNumber] = state.attributes[attr.id] === "" ? 'false' : state.attributes[attr.id];
        });

        let doesNotContributeAttributes = state.label.attributes.filter((attr: any) =>
            attr.name.startsWith(checkPhase) && attr.name.endsWith('doesnotcontribute'),
        );
        doesNotContributeAttributes.forEach((attr: any) => {
            // get popup number
            let popupNumber = parseInt(attr.name.split('.')[2]);
            doesNotContributeValues[popupNumber] = state.attributes[attr.id] === "" ? 'false' : state.attributes[attr.id];
        });
        let bitwiseOr = doesNotAppearValues.map((val: any, index: number) => (val === 'true' ? true : false) || (doesNotContributeValues[index] === 'true' ? true : false));

        return bitwiseOr.every((val: any) => val === true);
    };

    return (
        <Layout.Sider
            className='cvat-objects-sidebar'
            theme='light'
            width={
                toDisplayQuestions && ['phase2a', 'phase3a', 'phase3b'].includes(jobInstance.phase)
                    ? '50vw'
                    : ['phase2b'].includes(jobInstance.phase)
                    ? '20vw'
                    : '50vw'
            }
            collapsedWidth={0}
            reverseArrow
            trigger={null}
        >
            {['phase2a', 'phase2b', 'phase3a'].includes(jobInstance.phase) && (
                <div className='cvat-auto-popup-strip'>
                    <div className='cvat-auto-popup-title'>Popup Checkpoints</div>
                    <AutoPopup></AutoPopup>
                </div>
            )}

            {toDisplayQuestions && ['phase2a', 'phase3a', 'phase3b'].includes(jobInstance.phase) && (
                <Collapse className='cvat-question-sidebar-collapse' accordion>
                    {people.map((person, idx) => (
                        <Collapse.Panel
                            key={idx}
                            collapsible={doesPersonContribute(person) && !personDoesNotConributeOrAppear(person) ? 'header' : 'disabled'}
                            collapsed={!doesPersonContribute(person) || personDoesNotConributeOrAppear(person)}
                            header={
                                <>
                                    <Row style={{ width: '100%' }}>
                                        <Col span={6}>
                                            {person.objectType === 'audioselection' && audioBlobURL && (
                                                <>
                                                    <AudioPlayButton
                                                        audioBlobURL={audioBlobURL}
                                                        audio={audio}
                                                        audioPlayingIdx={audioPlayingIdx}
                                                        frameRate={frameRate}
                                                        startFrame={jobInstance.startFrame}
                                                        audiolisteners={audiolisteners}
                                                        idx={idx}
                                                        playAudio={playAudio}
                                                        setAudioPlayingIdx={setAudioPlayingIdx}
                                                        state={person}
                                                    />
                                                </>
                                            )}
                                            {person.objectType === 'shape' &&
                                                croppedImageMap &&
                                                croppedImageMap[person.clientID] && (
                                                    <img
                                                        style={{ maxWidth: '100%', height: '150px' }}
                                                        src={croppedImageMap[person.clientID]}
                                                    ></img>
                                                )}
                                        </Col>

                                        <Col span={17}>
                                            <h4 style={{ textAlign: 'center' }}>
                                                {getLabelDisplayName(person.label.name)}
                                                {
                                                    person.attributes[
                                                        person.label.attributes?.find(
                                                            (attr: any) => attr.name === 'identifier',
                                                        ).id
                                                    ]
                                                }
                                                {!doesPersonContribute(person) && !personDoesNotConributeOrAppear &&
                                                    ' (Does not contribute meaningfully to this interaction)'}
                                                {personDoesNotConributeOrAppear(person) &&
                                                    ' (Does not appear or Does not contribute meaningfully in this interaction)'}
                                            </h4>
                                        </Col>
                                        <Col span={1}>
                                            {person.label.attributes
                                                .filter(
                                                    (attr: any) =>
                                                        attr.name.startsWith(`${jobInstance.phase}.sidebar.`) &&
                                                        !attr.name.endsWith('.time') &&
                                                        toDisplay(
                                                            person.label.attributes,
                                                            jobAttributeSpec,
                                                            person.attributes,
                                                            jobAttributes,
                                                            attr,
                                                            people,
                                                        ),
                                                )
                                                .some((attr: any) => person.attributes[attr.id] === '') &&
                                                doesPersonContribute(person) &&
                                                !personDoesNotConributeOrAppear(person) && (
                                                    <CaretLeftFilled style={{ color: 'red' }} />
                                                )}
                                        </Col>
                                    </Row>
                                </>
                            }
                        >
                            {doesPersonContribute(person) && (
                                <Collapse className='cvat-accordion-collapse-scroller' defaultActiveKey={[0]} accordion>
                                    {person.label.attributes
                                        .filter(
                                            (attr: any) =>
                                                attr.name.startsWith(`${jobInstance.phase}.sidebar.`) &&
                                                !attr.name.endsWith('.time') &&
                                                toDisplay(
                                                    person.label.attributes,
                                                    jobAttributeSpec,
                                                    person.attributes,
                                                    jobAttributes,
                                                    attr,
                                                    people,
                                                ),
                                            // sort based on order in annotation-conf
                                        )
                                        .sort((a: any, b: any) => {
                                            const orderA = Object.keys(phaseConf).indexOf(a.name);
                                            const orderB = Object.keys(phaseConf).indexOf(b.name);
                                            return orderA - orderB;
                                        })
                                        .map((attr: any, attrIdx: number) => (
                                            <Collapse.Panel
                                                key={attrIdx}
                                                header={
                                                    <>
                                                        {phaseConf[attr.name]?.shortText || attr.name}
                                                        {person.attributes[attr.id] === '' && !attr.nullable && (
                                                            <CaretLeftFilled style={{ color: 'red' }} />
                                                        )}
                                                    </>
                                                }
                                            >
                                                <div>
                                                    <div style={{ margin: '25px 0 0 0' }}>
                                                        <Text>{getFilledFullText(attr.name, person.label.name)}</Text>
                                                    </div>
                                                    <div>
                                                        {attr.inputType === 'select' && (
                                                            <Select
                                                                options={attr.values.map((val: string) => ({
                                                                    label: val,
                                                                    value: val,
                                                                }))}
                                                                onChange={(val) => {
                                                                    person.attributes = {
                                                                        ...person.attributes,
                                                                        [attr.id]: val,
                                                                    };
                                                                    onUpdateAnnotations([people[idx]]);
                                                                }}
                                                                value={person.attributes[attr.id]}
                                                                style={{ width: '100%' }}
                                                            />
                                                        )}
                                                        {attr.inputType === 'multiselect' && (
                                                            <Select
                                                                mode='multiple'
                                                                options={attr.values.map((val: string) => ({
                                                                    label: val,
                                                                    value: val,
                                                                }))}
                                                                onChange={(val: string[]) => {
                                                                    person.attributes = {
                                                                        ...person.attributes,
                                                                        [attr.id]: val
                                                                            .map((valitem: string) => valitem.trim())
                                                                            .join(),
                                                                    };
                                                                    onUpdateAnnotations([people[idx]]);
                                                                }}
                                                                value={
                                                                    person.attributes[attr.id]
                                                                        ? person.attributes[attr.id]
                                                                              .split(',')
                                                                              .filter(
                                                                                  (valitem: string) =>
                                                                                      valitem.trim() !== '',
                                                                              )
                                                                        : []
                                                                }
                                                                style={{ width: '100%' }}
                                                            />
                                                        )}
                                                        {attr.inputType === 'text' && (
                                                            <AnnotationVoiceInput
                                                                value={textboxStates[attr.id]}
                                                                onChange={(nextValue) => {
                                                                    handleTextboxChange(attr.id, nextValue, idx);
                                                                }}
                                                                context={{
                                                                    jobId: jobInstance.id,
                                                                    phase: jobInstance.phase,
                                                                    attributeSpecId: attr.id,
                                                                    attributeName: attr.name,
                                                                    targetScope: 'object',
                                                                    targetServerId: person.serverID,
                                                                }}
                                                            />
                                                        )}
                                                        {attr.inputType === 'number' && (
                                                            // <InputNumber
                                                            //     value={person.attributes[attr.id]}
                                                            //     min={parseFloat(attr.values[0]) || 0}
                                                            //     max={parseFloat(attr.values[1]) || 100}
                                                            //     step={parseFloat(attr.values[2]) || 1}
                                                            //     onChange={(val) => {
                                                            //         person.attributes = {
                                                            //             ...person.attributes,
                                                            //             [attr.id]: val + '',
                                                            //         };
                                                            //         onUpdateAnnotations([people[idx]]);
                                                            //     }}
                                                            // />
                                                            <>
                                                                <Row gutter={[16, 32]}>
                                                                    <Col offset={4} sm={16}>
                                                                        <Slider
                                                                            included={false}
                                                                            marks={{
                                                                                ...Array(
                                                                                    (parseFloat(attr.values[1]) || 5) -
                                                                                        (parseFloat(attr.values[0]) ||
                                                                                            1) +
                                                                                        1,
                                                                                )
                                                                                    .fill(0)
                                                                                    .reduce((prev, item, idx) => {
                                                                                        prev[
                                                                                            idx +
                                                                                                (parseFloat(
                                                                                                    attr.values[0],
                                                                                                ) || 1)
                                                                                        ] = '';
                                                                                        return prev;
                                                                                    }, {}),

                                                                                [parseFloat(attr.values[0]) || 1]:
                                                                                    phaseConf[attr.name]?.low || '',
                                                                                [((parseFloat(attr.values[0]) || 1) +
                                                                                    (parseFloat(attr.values[1]) || 5)) /
                                                                                2]: 'Neutral',
                                                                                [parseFloat(attr.values[1]) || 5]:
                                                                                    phaseConf[attr.name]?.high || '',
                                                                            }}
                                                                            min={parseFloat(attr.values[0]) || 1}
                                                                            max={parseFloat(attr.values[1]) || 5}
                                                                            step={parseFloat(attr.values[2]) || 1}
                                                                            onChange={(val) => {
                                                                                person.attributes = {
                                                                                    ...person.attributes,
                                                                                    [attr.id]: '' + val,
                                                                                };
                                                                                onUpdateAnnotations([people[idx]]);
                                                                            }}
                                                                            value={
                                                                                person.attributes[attr.id] != ''
                                                                                    ? parseInt(
                                                                                          person.attributes[attr.id],
                                                                                      )
                                                                                    : 3
                                                                            }
                                                                            style={{ width: '100%' }}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                            </>
                                                        )}
                                                    </div>
                                                    {person.label.attributes
                                                        .filter((attr2: any) => attr2.name == attr.name + '.time')
                                                        .map((timeAttr: any, jdx: number) => (
                                                            <>
                                                                {person.attributes[attr.id] ===
                                                                    phaseConf[attr.name]?.affirmativeOption && (
                                                                    <BuildTimeList
                                                                        rawValue={person.attributes[timeAttr.id]}
                                                                        key={jdx}
                                                                        updateTimelist={(timelist) => {
                                                                            person.attributes = {
                                                                                ...person.attributes,
                                                                                [timeAttr.id]: JSON.stringify(timelist),
                                                                            };
                                                                            onUpdateAnnotations([person]);
                                                                        }}
                                                                    ></BuildTimeList>
                                                                )}
                                                            </>
                                                        ))}
                                                </div>
                                            </Collapse.Panel>
                                        ))}
                                </Collapse>
                            )}
                        </Collapse.Panel>
                    ))}
                    <Collapse.Panel
                        key={999}
                        header={
                            <>
                                <Row style={{ width: '100%' }}>
                                    <Col sm={22}>
                                        <h4 style={{ textAlign: 'center' }}>Situation Labels</h4>
                                    </Col>
                                    <Col sm={2}>
                                        {jobAttributeSpec
                                            .filter(
                                                (attr: any) =>
                                                    attr.name.startsWith(`${jobInstance.phase}.sidebar.`) &&
                                                    !attr.name.endsWith('.time') &&
                                                    toDisplay(
                                                        jobAttributeSpec,
                                                        jobAttributeSpec,
                                                        jobAttributes,
                                                        jobAttributes,
                                                        attr,
                                                        people,
                                                    ),
                                            )
                                            .some((attr: any) => jobAttributes[attr.id] === '') && (
                                            <CaretLeftFilled style={{ color: 'red' }} />
                                        )}
                                    </Col>
                                </Row>
                            </>
                        }
                    >
                        {
                            <Collapse className='cvat-accordion-collapse-scroller' defaultActiveKey={[0]} accordion>
                                {jobAttributeSpec
                                    .filter(
                                        (attr: any) =>
                                            attr.name.startsWith(`${jobInstance.phase}.sidebar.`) &&
                                            !attr.name.endsWith('.time') &&
                                            toDisplay(
                                                jobAttributeSpec,
                                                jobAttributeSpec,
                                                jobAttributes,
                                                jobAttributes,
                                                attr,
                                                people,
                                            ),
                                    )
                                    .sort((a: any, b: any) => {
                                        const orderA = Object.keys(phaseConf).indexOf(a.name);
                                        const orderB = Object.keys(phaseConf).indexOf(b.name);
                                        return orderA - orderB;
                                    })
                                    .map((attr: any, attrIdx: number) => (
                                        <Collapse.Panel
                                            key={attrIdx}
                                            header={
                                                <>
                                                    {phaseConf[attr.name]?.shortText || attr.name}
                                                    {jobAttributes[attr.id] === '' && !attr.nullable && (
                                                        <CaretLeftFilled style={{ color: 'red' }} />
                                                    )}
                                                </>
                                            }
                                        >
                                            <div>
                                                <div style={{ margin: '25px 0 0 0' }}>
                                                    <Text>{phaseConf[attr.name]?.fullText}</Text>
                                                </div>
                                                <div>
                                                    {attr.inputType === 'select' && (
                                                        <Select
                                                            options={attr.values.map((val: string) => ({
                                                                label: val,
                                                                value: val,
                                                            }))}
                                                            onChange={(val) => {
                                                                onUpdateJobAttributes(attr.id, val);
                                                            }}
                                                            value={jobAttributes[attr.id]}
                                                            style={{ width: '100%' }}
                                                        />
                                                    )}

                                                    {attr.inputType === 'text' && (
                                                        <AnnotationVoiceInput
                                                            value={jobAttributes[attr.id]}
                                                            onChange={(nextValue) => {
                                                                onUpdateJobAttributes(attr.id, nextValue);
                                                            }}
                                                            context={{
                                                                jobId: jobInstance.id,
                                                                phase: jobInstance.phase,
                                                                attributeSpecId: attr.id,
                                                                attributeName: attr.name,
                                                                targetScope: 'job',
                                                                targetServerId: null,
                                                            }}
                                                        />
                                                    )}
                                                    {attr.inputType === 'number' && (
                                                        // <InputNumber
                                                        //     value={jobAttributes[attr.id]}
                                                        //     min={parseFloat(attr.values[0]) || 0}
                                                        //     max={parseFloat(attr.values[1]) || 100}
                                                        //     step={parseFloat(attr.values[2]) || 1}
                                                        //     onChange={(val) => {
                                                        //         onUpdateJobAttributes(attr.id, val + '');
                                                        //     }}
                                                        // />
                                                        <>
                                                            <Row gutter={[16, 32]}>
                                                                <Col offset={4} sm={16}>
                                                                    <Slider
                                                                        included={false}
                                                                        marks={{
                                                                            ...Array(
                                                                                (parseFloat(attr.values[1]) || 5) -
                                                                                    (parseFloat(attr.values[0]) || 1) +
                                                                                    1,
                                                                            )
                                                                                .fill(0)
                                                                                .reduce((prev, item, idx) => {
                                                                                    prev[
                                                                                        idx +
                                                                                            (parseFloat(
                                                                                                attr.values[0],
                                                                                            ) || 1)
                                                                                    ] = '';
                                                                                    return prev;
                                                                                }, {}),

                                                                            [parseFloat(attr.values[0]) || 1]:
                                                                                phaseConf[attr.name]?.low || '',
                                                                            [((parseFloat(attr.values[0]) || 1) +
                                                                                (parseFloat(attr.values[1]) || 5)) /
                                                                            2]: 'Neutral',
                                                                            [parseFloat(attr.values[1]) || 5]:
                                                                                phaseConf[attr.name]?.high || '',
                                                                        }}
                                                                        min={parseFloat(attr.values[0]) || 1}
                                                                        max={parseFloat(attr.values[1]) || 5}
                                                                        step={parseFloat(attr.values[2]) || 1}
                                                                        onChange={(val) => {
                                                                            onUpdateJobAttributes(attr.id, val + '');
                                                                        }}
                                                                        value={
                                                                            jobAttributes[attr.id] != ''
                                                                                ? parseInt(jobAttributes[attr.id])
                                                                                : 3
                                                                        }
                                                                        style={{ width: '100%' }}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                            <Row style={{ height: '32px' }}></Row>
                                                        </>
                                                    )}
                                                    {
                                                        attr.inputType === 'multiselect' && (
                                                            <Select
                                                                mode='multiple'
                                                                options={attr.values.map((val: string) => ({
                                                                    label: val,
                                                                    value: val,
                                                                }))}
                                                                onChange={(val: string[]) => {
                                                                    // update the state
                                                                    onUpdateJobAttributes(attr.id, val.join());
                                                                }}
                                                                value={
                                                                    jobAttributes[attr.id]
                                                                        ? jobAttributes[attr.id].split(',')
                                                                        : []
                                                                }
                                                                style={{ width: '100%' }}
                                                            />
                                                        )
                                                    }
                                                </div>
                                                {jobAttributeSpec
                                                    .filter((attr2: any) => attr2.name == attr.name + '.time')
                                                    .map((timeAttr: any, jdx: number) => (
                                                        <>
                                                            {jobAttributes[attr.id] ===
                                                                phaseConf[attr.name]?.affirmativeOption && (
                                                                <BuildTimeList
                                                                    rawValue={jobAttributes[timeAttr.id]}
                                                                    key={jdx}
                                                                    updateTimelist={(timelist) => {
                                                                        onUpdateJobAttributes(
                                                                            timeAttr.id,
                                                                            JSON.stringify(timelist),
                                                                        );
                                                                    }}
                                                                ></BuildTimeList>
                                                            )}
                                                        </>
                                                    ))}
                                            </div>
                                        </Collapse.Panel>
                                    ))}
                            </Collapse>
                        }
                    </Collapse.Panel>
                </Collapse>
            )}
        </Layout.Sider>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(QuestionsSideBar));
